import { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { ALLOWED_STORE_KEY, ATTRIBUTION_KEYS_REGEX, GA_VARIABLES, getUTMFromStorage, saveUTMToStorage, SNOWPLOW_TRIGGER, SNOWPLOW_USER_CUSTOMER_CONTEXT, snowplowEvent } from 'Utilities/analytics';
import { setKameleoonCustomData } from 'Utilities/analytics/kameleoon/customData';
import { getGACid } from 'Utilities/analytics/setGAData';
import { initBroadcast, postBroadcast } from 'Utilities/broadcastChannel';
import { trackUserCookie } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';
import { PATH_UNSUBSCRIBE } from 'Utilities/routing';
import { useRefreshAccessToken } from 'Utilities/user/hooks/useRefreshAccessToken';
import useScrubQueryParam from 'Utilities/useScrubQueryParam';

const Init = () => {
  const { showCognitoLogin } = useFeatureFlags();
  const { userActions, userState: user } = useContext(UserContext);
  const { configActions: actions } = useContext(ConfigContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { setCognitoRefreshTokenInterval } = useRefreshAccessToken();

  const userActionsRef = useRef(userActions);
  const configActions = useRef(actions);
  const userSignedInRef = useRef(Number.isInteger(user?.id));

  const history = useHistory();
  const tokenLogin = useRef(new URLSearchParams(history.location.search).get('token'));

  const scrubbedToken = useScrubQueryParam(
    history.location.pathname.startsWith(`/${PATH_UNSUBSCRIBE.split('/')[1]}`)
      ? null
      : 'token',
  );

  const userSignedIn = userSignedInRef.current;

  useEffect(() => {
    // Get GCLID
    let gclid;
    const url = history.location.href;
    const parameterName = 'gclid'.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${parameterName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) {
      gclid = null;
    } else if (!results[2]) {
      gclid = '';
    } else {
      gclid = decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    configActions.current.update({ gclid });

    // Make utm params available to GTM
    const qsArray = Object.entries(Object.fromEntries(new URLSearchParams(history.location.search)))
      .filter(([k]) => ATTRIBUTION_KEYS_REGEX.test(k))
      .filter(([, v]) => v)
      .map(([k, v]) => [k?.toLowerCase(), v])
      .sort((a, b) => a?.[0].localeCompare(b?.[0])) || [];

    const previousUTMParams = getUTMFromStorage() || {};

    let previousVisitTime;
    let utmParamsFromStore = [];

    if (Array.isArray(previousUTMParams)) {
      utmParamsFromStore = previousUTMParams;
    } else if (previousUTMParams?.qsArray) {
      previousVisitTime = previousUTMParams?.time;
      utmParamsFromStore = previousUTMParams?.qsArray;
    }

    const hasAttributionQueryParams = (qs) => Array.isArray(qs) && qs.length;

    const TIME_NOW = new Date().getTime();

    const snowplowTimeDelta = (previousTime = previousVisitTime) => {
      if (Number.isInteger(previousTime)) {
        if (TIME_NOW - previousTime >= 5000) {
          SNOWPLOW_TRIGGER('newSession');
        }
      } else {
        SNOWPLOW_TRIGGER('newSession');
      }
    };

    // Don't fire a new session if `onSessionUpdateCallback` has been called within the last 5 seconds
    // eslint-disable-next-line no-underscore-dangle
    if (!globalThis.__SP_SESSION_TIME__ || TIME_NOW - globalThis.__SP_SESSION_TIME__ >= 5000) {
      if (hasAttributionQueryParams(qsArray) && hasAttributionQueryParams(utmParamsFromStore)) {
        if (JSON.stringify(qsArray) !== JSON.stringify(utmParamsFromStore)) {
          // Fire new session if utm params have changed after 5 secs
          snowplowTimeDelta();
        }
      } else if (Number.isInteger(previousVisitTime) && Array.isArray(qsArray) && Array.isArray(utmParamsFromStore)) {
        if (utmParamsFromStore?.length === 0 && hasAttributionQueryParams(qsArray)) {
          // Fire new session if utm params have been added after 5 secs from previous visit with no UTMs
          snowplowTimeDelta();
        }/* else if (qsArray?.length === 0 && hasAttributionQueryParams(utmParamsFromStore)) {
          // Fire new session if utm params have been removed after 5 secs from previous visit with UTMs
          snowplowTimeDelta();
        } */
      }
    }

    saveUTMToStorage(qsArray);

    (qsArray || utmParamsFromStore)?.forEach(([k, v]) => {
      if (Object.values(ALLOWED_STORE_KEY).includes(v)) {
        GA_VARIABLES[k] = v;
      }
    });

    const isReferredUser = [...qsArray, ...utmParamsFromStore]
      .some(([k, v]) => k === 'utm_source' && v === ALLOWED_STORE_KEY.MENTON_ME);

    userActionsRef.current.update({ isReferredUser });
  }, [history]);

  useEffect(() => {
    if (showCognitoLogin && Number.isInteger(user?.id) && user?.cognitoId) {
      setCognitoRefreshTokenInterval();
    }
  }, [showCognitoLogin, user?.id, user?.cognitoId, setCognitoRefreshTokenInterval]);

  useEffect(() => {
    initBroadcast();
  }, []);

  useEffect(() => {
    if (userSignedIn && scrubbedToken) {
      trackUserCookie(user);
      postBroadcast(); // Sign in any open background tabs
    }
  }, [scrubbedToken, userSignedIn]);

  useEffect(() => {
    // Populate GA before any child useEffect hooks call GA
    if (userSignedIn) {
      GA_VARIABLES.enquiryId = vehicle?.enquiry?.id;
      GA_VARIABLES.userId = user?.id;

      SNOWPLOW_USER_CUSTOMER_CONTEXT({
        login_persisted: tokenLogin.current,
        ...user,
      });
    }
  }, [vehicle?.enquiry?.id, user, userSignedIn]);

  useEffect(() => {
    SNOWPLOW_TRIGGER('trackPageView');
  }, []);

  useEffect(() => {
    const kameleoonSnowplowCustomData = () => {
      SNOWPLOW_TRIGGER(function () {
        const sp = this.sp1;
        const domainUserInfo = sp.getDomainUserInfo();

        if (Array.isArray(domainUserInfo) && domainUserInfo.length) {
          // https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/javascript-tracker/javascript-tracker-v3/advanced-usage/tracker-callbacks/#getdomainuserinfo
          setKameleoonCustomData('Snowplow Domain User ID', domainUserInfo[1]);
          setKameleoonCustomData('Snowplow Domain Session ID', domainUserInfo[6]);
        }
      });
    };

    const handler = () => {
      kameleoonSnowplowCustomData();
    };

    document.addEventListener('snowplowSessionUpdate', handler, { passive: true });

    kameleoonSnowplowCustomData();

    return () => {
      document.removeEventListener('snowplowSessionUpdate', handler);
    };
  }, []);

  useEffect(() => {
    setKameleoonCustomData('Motorway User ID', user?.id);
  }, [user?.id]);

  useEffect(() => {
    const gaCid = getGACid();

    if (gaCid) {
      setKameleoonCustomData('Google Client ID', `${gaCid}`);
    }
  }, []);

  useEffect(() => {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.loadEventEnd) {
            observer.disconnect();

            snowplowEvent({
              data: entry.toJSON(),
              schema: 'page_timing',
              version: '1-0-1',
            });
          }
        });
      });

      // Adding the `buffered: true` option will prevent safari from firing subsequent events
      observer.observe({ type: 'navigation' });

      return () => observer.disconnect();
    } catch (err) {
      window?.Sentry?.captureException?.(new Error(err), {
        level: 'warning',
      });
    }

    return () => {};
  }, []);

  return (null);
};

export default Init;
